import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getStoreSummary } from '../api/api';
import { DollarSign, CreditCard, Banknote, Phone, Receipt, Coins, TrendingUp, TrendingDown, Wallet, Calendar, ChevronLeft, ChevronRight, PiggyBank, Lock, Unlock } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import PrivacyBlur, { PrivacyNoticeBanner } from '../components/PrivacyBlur';
import { isCashierRole } from '../utils/privacyUtils';

const CashierDashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const PAYMENT_COLORS = {
    'POS': '#1e40af',
    'Efectivo': '#059669',
    'Recargas': '#d97706',
    'Recaudo': '#7c3aed',
    'Sobrantes': '#dc2626'
  };

  const PAYMENT_ICONS = {
    'POS': CreditCard,
    'Efectivo': Banknote,
    'Recargas': Phone,
    'Recaudo': Receipt,
    'Sobrantes': Coins
  };

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    loadSummary();
  }, [selectedMonth, selectedYear]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const data = await getStoreSummary(selectedYear, selectedMonth);
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Check if viewing current month (today's data)
  const isCurrentMonth = () => {
    const now = new Date();
    return selectedMonth === (now.getMonth() + 1) && selectedYear === now.getFullYear();
  };

  // For cashiers: ALWAYS blur summary/historical data
  // Because monthly summaries include data from previous days
  // Cashiers should only see TODAY's real-time balance clearly
  const isCashier = isCashierRole(user);
  
  // Blur monthly summaries for cashiers (even current month has historical days)
  const shouldBlurMonthlySummary = isCashier;
  
  // Blur completely if viewing previous months
  const shouldBlurPreviousMonth = isCashier && !isCurrentMonth();

  // Check if user has permission
  const hasPermission = (permission) => {
    return user?.permissions?.[permission] !== false;
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pieData = summary?.sales_by_method ? 
    Object.entries(summary.sales_by_method)
      .filter(([_, data]) => data.total > 0)
      .map(([method, data]) => ({
        name: method,
        value: data.total
      })) : [];

  const barData = summary?.sales_by_method ?
    Object.entries(summary.sales_by_method).map(([method, data]) => ({
      name: method,
      total: data.total,
      count: data.count
    })) : [];

  return (
    <div className="space-y-6">
      {/* Privacy Notice Banner for Cashiers - Always show for cashiers */}
      {isCashier && (
        <PrivacyNoticeBanner show={true} />
      )}

      {/* Header con selector de mes */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl shadow-xl p-6 text-white border border-slate-600/30">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Mi Dashboard</h1>
            <p className="text-slate-300">{summary?.store?.name}</p>
          </div>
          
          {/* Selector de Mes */}
          <div className="flex items-center space-x-3 bg-slate-600/50 rounded-lg px-4 py-2 border border-slate-500/50">
            <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-500 rounded">
              <ChevronLeft size={20} />
            </button>
            <div className="text-center min-w-[140px]">
              <p className="text-sm text-slate-300">Período</p>
              <p className="font-bold">{months[selectedMonth - 1]} {selectedYear}</p>
            </div>
            <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-500 rounded">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Tarjeta Principal: Saldo TOTAL en Caja - SIEMPRE VISIBLE (es tiempo real) */}
      {hasPermission('view_real_balance') && (
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl shadow-xl p-6 text-white">
          {/* Saldo Actual - VISIBLE para todos */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Unlock size={24} />
                <h2 className="text-lg font-semibold">💰 Saldo Disponible Ahora</h2>
              </div>
              <p className="text-emerald-100 text-sm mb-3">Tu saldo en caja en tiempo real</p>
              <p className="text-4xl font-bold">
                {summary?.store?.currency}{summary?.available_cash?.saldo_total?.toLocaleString() || '0.00'}
              </p>
            </div>
            <div className="bg-white/20 rounded-xl p-4">
              <PiggyBank size={48} />
            </div>
          </div>
          
          {/* Desglose del Saldo - BORROSO para cajeros (datos mensuales/históricos) */}
          <PrivacyBlur isBlurred={shouldBlurMonthlySummary}>
            <div className="mt-6 space-y-3">
              {/* Saldo Inicial del Mes (acumulado de meses anteriores) */}
              <div className="bg-blue-500/30 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-blue-200">📅 Saldo Inicial del Mes</p>
                    <p className="text-xs text-blue-100 opacity-75">(acumulado de meses anteriores)</p>
                  </div>
                  <p className="text-xl font-bold">{summary?.store?.currency}{summary?.available_cash?.saldo_inicial_mes?.toLocaleString() || '0'}</p>
                </div>
              </div>
            
              {/* Ingresos del mes */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs text-emerald-200">Efectivo</p>
                  <p className="text-sm font-bold">+{summary?.store?.currency}{summary?.available_cash?.breakdown?.efectivo?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs text-emerald-200">Recargas</p>
                  <p className="text-sm font-bold">+{summary?.store?.currency}{summary?.available_cash?.breakdown?.recargas?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs text-emerald-200">Recaudo</p>
                  <p className="text-sm font-bold">+{summary?.store?.currency}{summary?.available_cash?.breakdown?.recaudo?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs text-emerald-200">Sobrante</p>
                  <p className="text-sm font-bold">+{summary?.store?.currency}{summary?.available_cash?.breakdown?.sobrante?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs text-emerald-200">Transferencia</p>
                  <p className="text-sm font-bold">+{summary?.store?.currency}{summary?.available_cash?.breakdown?.transferencia?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-red-500/30 rounded-lg p-2 text-center">
                  <p className="text-xs text-red-200">Gastos</p>
                  <p className="text-sm font-bold">-{summary?.store?.currency}{summary?.available_cash?.expenses?.toLocaleString() || '0'}</p>
                </div>
              </div>
            
              {/* Solo del mes */}
              <div className="bg-white/10 rounded-lg p-3 mt-2">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-emerald-200">Movimiento Neto del Mes</p>
                  <p className="text-lg font-bold">{summary?.store?.currency}{summary?.available_cash?.real_available?.toLocaleString() || '0'}</p>
                </div>
              </div>
            </div>
          </PrivacyBlur>
        </div>
      )}

      {/* Tarjeta de Ventas POS - No disponible (va al banco) */}
      {hasPermission('view_sales_report') && (
        <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-blue-600">
          <PrivacyBlur isBlurred={shouldBlurMonthlySummary}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Lock size={16} className="text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase font-semibold">No disponible (Va al Banco)</span>
                </div>
                <h3 className="text-lg font-bold text-slate-800">Ventas POS</h3>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {summary?.store?.currency}{summary?.available_cash?.breakdown?.pos?.toLocaleString() || '0.00'}
              </p>
              <p className="text-sm text-slate-500">{summary?.sales_by_method?.POS?.count || 0} transacciones</p>
            </div>
            <div className="bg-blue-100 rounded-xl p-3">
              <CreditCard size={32} className="text-blue-600" />
            </div>
          </div>
          </PrivacyBlur>
        </div>
      )}

      {/* Tarjetas de Métodos de Pago */}
      {hasPermission('view_monthly_summary') && (
        <PrivacyBlur isBlurred={shouldBlurMonthlySummary}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(summary?.sales_by_method || {}).map(([method, data]) => {
              const Icon = PAYMENT_ICONS[method] || Receipt;
              const color = PAYMENT_COLORS[method] || '#64748b';
              const isTouchable = ['Efectivo', 'Recargas', 'Recaudo', 'Sobrantes'].includes(method);
              
              return (
                <div 
                  key={method} 
                  className="bg-white rounded-lg shadow-md p-4 border-l-4 hover:shadow-lg transition-all"
                  style={{ borderLeftColor: color }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="rounded-lg p-2" style={{ backgroundColor: `${color}15` }}>
                      <Icon size={20} style={{ color }} />
                    </div>
                    {isTouchable ? (
                      <Unlock size={14} className="text-emerald-500" />
                    ) : (
                      <Lock size={14} className="text-slate-400" />
                    )}
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm">{method}</h4>
                  <p className="text-xl font-bold mt-1" style={{ color }}>
                    {summary?.store?.currency}{data.total.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">{data.count} ventas</p>
                </div>
              );
            })}
          </div>
        </PrivacyBlur>
      )}

      {/* Resumen y Gráficas */}
      {hasPermission('view_charts') && (
        <PrivacyBlur isBlurred={shouldBlurMonthlySummary}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Resumen General */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Resumen del Mes</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="text-emerald-600" size={24} />
                    <span className="font-medium text-slate-700">Total Ventas</span>
                  </div>
                  <span className="text-xl font-bold text-emerald-600">
                  {summary?.store?.currency}{summary?.summary?.total_sales?.toLocaleString() || '0'}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <TrendingDown className="text-red-600" size={24} />
                  <span className="font-medium text-slate-700">Total Gastos</span>
                </div>
                <span className="text-xl font-bold text-red-600">
                  -{summary?.store?.currency}{summary?.summary?.total_expenses?.toLocaleString() || '0'}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="flex items-center space-x-3">
                  <DollarSign className="text-blue-600" size={24} />
                  <span className="font-bold text-slate-700">Ingreso Neto</span>
                </div>
                <span className={`text-xl font-bold ${summary?.summary?.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {summary?.store?.currency}{summary?.summary?.net_income?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
          </div>

          {/* Gráfica de Pastel */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Distribución de Ventas</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PAYMENT_COLORS[entry.name] || '#64748b'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${summary?.store?.currency}${value.toLocaleString()}`, 'Total']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No hay datos para mostrar
              </div>
            )}
          </div>
        </div>
        </PrivacyBlur>
      )}

      {/* Fondo de Sencillo */}
      {hasPermission('view_petty_cash') && (
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl shadow-xl p-6 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <Wallet size={24} />
            <h3 className="text-lg font-bold">Fondo de Sencillo</h3>
          </div>
          <p className="text-slate-300 text-sm">
            Para ver y gestionar el fondo de sencillo, dirígete a la sección de{' '}
            <a href="/petty-cash" className="text-blue-400 hover:text-blue-300 underline">Fondo Sencillo</a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default CashierDashboard;
